import Linkify from 'linkify-react';
import type { ReactNode } from 'react';
import { memo } from 'react';
import { twMerge } from 'tailwind-merge';

import { Logo } from '@/components/Logo';

import FlagDropdown from './FlagDropdown';
import NoteMenu from './NoteMenu';

const Message = ({
  children,
  receipt,
  userInfo,
  className: classes,
}: {
  children?: ReactNode;
  receipt: API.MessageReceipt;
  userInfo?: boolean;
  className?: string;
}) => {
  const time = new Date(receipt.message.createdAt).toLocaleTimeString('en-us');

  const { message } = receipt;

  const boldMessages = ['GO', 'CANCEL', 'OPPOSITE', 'Set up', 'Setup'];

  const messageContent = boldMessages.some((boldMessage) =>
    message.content.trim().startsWith(boldMessage),
  ) ? (
    <span className="font-bold">{message.content}</span>
  ) : (
    <>{message.content}</>
  );

  return (
    <article
      data-receiptid={receipt.id}
      className={twMerge(
        'group relative flex w-full justify-start py-2 pr-4 align-top hover:bg-zinc-50 dark:hover:bg-white/5',
        classes,
      )}
    >
      <div className="mx-2 flex w-14 shrink-0 flex-col items-center justify-start pt-1">
        {userInfo ? (
          <Logo className="h-8 w-8 select-none rounded" />
        ) : (
          <time className="pt-px text-[9px] text-transparent group-hover:text-zinc-700 dark:group-hover:text-zinc-400">
            {time}
          </time>
        )}
      </div>
      <div className="w-full">
        {userInfo && (
          <div>
            <span className="text-sm font-bold dark:text-zinc-200">RAS</span>
            <time className="ml-2 text-xs text-zinc-500 dark:text-zinc-400">{time}</time>
          </div>
        )}
        <div className="space-y-3 text-[15px] leading-[1.375rem] dark:text-zinc-300 sm:text-sm">
          <Linkify>
            <p className="my-0 whitespace-pre-wrap break-words">{messageContent || ''}</p>
          </Linkify>
        </div>
        {children}

        <div
          className={twMerge(
            'absolute right-14 top-0 -mt-4 flex-row items-center divide-x rounded bg-white opacity-25 ring-1 ring-zinc-900/10 focus-within:flex focus-within:opacity-100 group-hover:flex group-hover:opacity-100 dark:divide-zinc-700 dark:bg-zinc-900 dark:shadow-none dark:shadow-white/5 dark:ring-white/10',
            !receipt.message.meta?.flags?.length && !receipt.message.meta?.note ? 'hidden' : 'flex',
          )}
        >
          <FlagDropdown id={receipt.message.id} flags={receipt.message.meta?.flags} />
          <NoteMenu id={receipt.message.id} note={receipt.message.meta?.note} />
        </div>
      </div>
    </article>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export default memo(
  Message,
  (prev, next) =>
    prev.receipt.message.content === next.receipt.message.content &&
    prev.receipt.message.visible === next.receipt.message.visible &&
    prev.receipt.release?.playText === next.receipt.release?.playText &&
    prev.receipt.release?.result === next.receipt.release?.result &&
    !!prev.receipt.message.meta === !!next.receipt.message.meta &&
    (!!prev.receipt.message.meta || !!next.receipt.message.meta
      ? JSON.stringify(prev.receipt.message.meta) === JSON.stringify(next.receipt.message.meta)
      : true),
);
